import React from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';
import { useDroppable } from '@dnd-kit/react';

type TaskColumnProps = {
  id: string;
  name: string;
  count: number;
  icon?: React.ReactNode;
  children?: React.ReactNode;
};

/**
 * A board column. This is a droppable container only — columns cannot be
 * reordered (their order is fully determined by the active grouping). Cards
 * are dropped here via the dnd-kit droppable API; empty columns remain valid
 * drop targets.
 */
export const TaskColumn: React.FC<TaskColumnProps> = observer(
  ({ id, name, count, icon, children }) => {
    const { ref, isDropTarget } = useDroppable({ id, type: 'column', accept: 'item' });

    return (
      <Container ref={ref} data-drop-target={isDropTarget}>
        <Header>
          {icon}
          <Name>{name}</Name>
          <Count>{count}</Count>
        </Header>

        <List>{children}</List>
      </Container>
    );
  },
);

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 0 0 auto;
  width: 30rem;
  min-width: 30rem;
  max-width: 30rem;
  scroll-snap-align: start;
  background-color: var(--color-surface-primary);
  border-radius: var(--card-border-radius);
  padding: 1.2rem;
  transition: background-color 0.2s ease;

  &[data-drop-target='true'] {
    background-color: var(--color-surface-secondary);
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.4rem;
  margin-bottom: 1.2rem;
`;

const Name = styled.span`
  font-size: 1.4rem;
  font-weight: 400;
  color: var(--color-texts-high-contrast);
  user-select: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Count = styled.span`
  font-size: 1.4rem;
  font-weight: 400;
  color: var(--color-texts-low-contrast);
  user-select: none;
  margin-inline-start: 0.4rem;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 4rem;
  overflow-y: auto;
`;
